/**
 * dataStoreSupabase.js - Supabase-backed Data Layer
 * 
 * This replaces the localStorage-based dataStore with Supabase cloud storage.
 * Data is loaded from Supabase on initialization and synced on changes.
 * 
 * Key differences from localStorage version:
 * - initializeData() is now async
 * - Data persists across all devices
 * - Some operations return Promises
 */

import {
  userService,
  roomService,
  scheduleService,
  ptoService,
  childService,
  childScheduleService,
  notesService,
  auditService,
  templateService,
  schedulePublishService
} from '../services/supabaseService';

// ============================================================================
// AUDIT ACTION TYPES
// ============================================================================

const AUDIT_ACTIONS = {
  USER_CREATED: 'USER_CREATED',
  USER_UPDATED: 'USER_UPDATED',
  USER_DELETED: 'USER_DELETED',
  PASSWORD_RESET: 'PASSWORD_RESET',
  PASSWORD_CHANGED: 'PASSWORD_CHANGED',
  AVAILABILITY_UPDATED: 'AVAILABILITY_UPDATED',
  USER_LOGIN: 'USER_LOGIN',
  USER_LOGOUT: 'USER_LOGOUT',
  LOGIN_FAILED: 'LOGIN_FAILED',
  ROOM_CREATED: 'ROOM_CREATED',
  ROOM_UPDATED: 'ROOM_UPDATED',
  ROOM_DELETED: 'ROOM_DELETED',
  SCHEDULE_ASSIGNMENT_ADDED: 'SCHEDULE_ASSIGNMENT_ADDED',
  SCHEDULE_ASSIGNMENT_REMOVED: 'SCHEDULE_ASSIGNMENT_REMOVED',
  SCHEDULE_CLEARED: 'SCHEDULE_CLEARED',
  SCHEDULE_PUBLISHED: 'SCHEDULE_PUBLISHED',
  SCHEDULE_UNPUBLISHED: 'SCHEDULE_UNPUBLISHED',
  PTO_REQUESTED: 'PTO_REQUESTED',
  PTO_APPROVED: 'PTO_APPROVED',
  PTO_DENIED: 'PTO_DENIED',
  PTO_CANCELLED: 'PTO_CANCELLED',
  CHILD_CREATED: 'CHILD_CREATED',
  CHILD_UPDATED: 'CHILD_UPDATED',
  CHILD_DELETED: 'CHILD_DELETED',
  CHILD_SCHEDULED: 'CHILD_SCHEDULED',
  CHILD_UNSCHEDULED: 'CHILD_UNSCHEDULED',
  CHILD_SCHEDULE_CLEARED: 'CHILD_SCHEDULE_CLEARED',
  CHILD_VISIT_SCHEDULED: 'CHILD_VISIT_SCHEDULED',
  CHILD_MARKED_ABSENT: 'CHILD_MARKED_ABSENT',
  CHILD_MARKED_PRESENT: 'CHILD_MARKED_PRESENT',
  TEMPLATE_CREATED: 'TEMPLATE_CREATED',
  TEMPLATE_UPDATED: 'TEMPLATE_UPDATED',
  TEMPLATE_DELETED: 'TEMPLATE_DELETED',
  TEMPLATE_APPLIED: 'TEMPLATE_APPLIED',
  ANNOUNCEMENT_CREATED: 'ANNOUNCEMENT_CREATED',
  ANNOUNCEMENT_UPDATED: 'ANNOUNCEMENT_UPDATED',
  ANNOUNCEMENT_DELETED: 'ANNOUNCEMENT_DELETED'
};

// ============================================================================
// LOCAL CACHE - Keeps data in memory for fast synchronous access
// ============================================================================

let cache = {
  users: [],
  rooms: [],
  schedule: {},
  ptoRequests: [],
  children: [],
  childSchedule: {},
  dailyNotes: {},
  roomDayNotes: {},
  auditLog: [],
  templates: [],
  publishedWeeks: {},
  initialized: false,
  loading: false
};

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize data from Supabase
 * Must be called and awaited before using the dataStore
 */
async function initializeData() {
  if (cache.initialized || cache.loading) return;
  cache.loading = true;
  
  console.log('Initializing data from Supabase...');
  
  try {
    // Load all data in parallel
    const [users, rooms, schedule, ptoRequests, children, childSchedule, dailyNotes, roomDayNotes, templates, auditLog, publishedWeeks] = await Promise.all([
      userService.getAll(),
      roomService.getAll(),
      scheduleService.getAll(),
      ptoService.getAll(),
      childService.getAll(),
      childScheduleService.getAll(),
      notesService.getDailyNotes(),
      notesService.getRoomDayNotes(),
      templateService.getAll(),
      auditService.getAll(),  // Fetches up to 5000 entries (90+ days of activity)
      schedulePublishService.getAll()
    ]);
    
    cache.users = users;
    cache.rooms = rooms;
    cache.schedule = schedule;
    cache.ptoRequests = ptoRequests;
    cache.children = children;
    cache.childSchedule = childSchedule;
    cache.dailyNotes = dailyNotes;
    cache.roomDayNotes = roomDayNotes;
    cache.templates = templates;
    cache.auditLog = auditLog;
    cache.publishedWeeks = publishedWeeks;
    cache.initialized = true;
    
    console.log('Data loaded from Supabase:', {
      users: users.length,
      rooms: rooms.length,
      children: children.length,
      templates: templates.length
    });
    
    // Run audit log cleanup in background (don't await - let it run async)
    auditService.cleanup(90).catch(err => console.error('Audit cleanup error:', err));
    
    // Run child schedule cleanup in background (don't await - let it run async)
    childScheduleService.cleanup(90).catch(err => console.error('Child schedule cleanup error:', err));
  } catch (error) {
    console.error('Error initializing data from Supabase:', error);
  } finally {
    cache.loading = false;
  }
}

/**
 * Check if data is initialized
 */
function isInitialized() {
  return cache.initialized;
}

/**
 * Wait for initialization to complete
 */
async function waitForInit() {
  while (cache.loading) {
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  if (!cache.initialized) {
    await initializeData();
  }
}


// ============================================================================
// USER METHODS
// ============================================================================

function getUsers() {
  return cache.users;
}

function getUserById(id) {
  return cache.users.find(u => u.id === id) || null;
}

function getUserByUsername(username) {
  return cache.users.find(u => u.username === username) || null;
}

async function createUser(userData, createdBy = null) {
  // Check for duplicate username
  if (cache.users.some(u => u.username === userData.username)) {
    throw new Error('Username already exists');
  }
  
  const newUser = await userService.create(userData);
  cache.users.push(newUser);
  
  // Log audit
  await addAuditLog({
    action: AUDIT_ACTIONS.USER_CREATED,
    userId: createdBy?.id,
    userName: createdBy?.name,
    targetType: 'user',
    targetId: newUser.id,
    details: { username: newUser.username, name: newUser.name, role: newUser.role }
  });
  
  return newUser;
}

async function updateUser(id, updates, updatedBy = null) {
  const existingUser = cache.users.find(u => u.id === id);
  const user = await userService.update(id, updates);
  const index = cache.users.findIndex(u => u.id === id);
  if (index >= 0) {
    cache.users[index] = { ...cache.users[index], ...user };
  }
  
  // Filter to only meaningful changes for audit log
  const meaningfulFields = ['name', 'username', 'role', 'email', 'phone', 'canViewAllStudents'];
  const meaningfulUpdates = {};
  let changeDescription = [];
  
  for (const field of meaningfulFields) {
    if (updates[field] !== undefined && updates[field] !== existingUser?.[field]) {
      meaningfulUpdates[field] = updates[field];
      if (field === 'role') {
        changeDescription.push(`role changed to ${updates[field] === 'ADMIN' ? 'Administrator' : 'Teacher'}`);
      } else if (field === 'name') {
        changeDescription.push(`name changed to "${updates[field]}"`);
      } else if (field === 'canViewAllStudents') {
        changeDescription.push(`"Can View All Students" ${updates[field] ? 'granted' : 'revoked'}`);
      } else {
        changeDescription.push(`${field} updated`);
      }
    }
  }
  
  // Only log if there were meaningful changes (skip preference-only updates)
  if (Object.keys(meaningfulUpdates).length > 0 || updates.availability) {
    await addAuditLog({
      action: AUDIT_ACTIONS.USER_UPDATED,
      userId: updatedBy?.id,
      userName: updatedBy?.name,
      targetType: 'user',
      targetId: id,
      details: { 
        targetName: existingUser?.name || 'Unknown',
        changes: changeDescription.length > 0 ? changeDescription.join(', ') : 'profile updated',
        ...meaningfulUpdates
      }
    });
  }
  
  return user;
}

async function deleteUser(id, deletedBy = null) {
  const user = cache.users.find(u => u.id === id);
  
  // Clear audit log references first to avoid foreign key constraint violation
  // (audit_log.user_id will become NULL, but user_name is preserved)
  await auditService.clearUserReferences(id);
  
  await userService.delete(id);
  cache.users = cache.users.filter(u => u.id !== id);
  
  await addAuditLog({
    action: AUDIT_ACTIONS.USER_DELETED,
    userId: deletedBy?.id,
    userName: deletedBy?.name,
    targetType: 'user',
    details: { username: user?.username, name: user?.name }
  });
  
  return true;
}

async function updateUserPassword(id, newPassword, updatedBy = null) {
  const targetUser = cache.users.find(u => u.id === id);
  
  // Update password in database (supabaseService will hash it)
  const updatedUser = await userService.update(id, { password: newPassword });
  
  // Update cache with the hashed password from the response
  const index = cache.users.findIndex(u => u.id === id);
  if (index >= 0) {
    cache.users[index].password = updatedUser.password;
  }
  
  await addAuditLog({
    action: AUDIT_ACTIONS.PASSWORD_RESET,
    userId: updatedBy?.id,
    userName: updatedBy?.name,
    targetType: 'user',
    targetId: id,
    details: { targetName: targetUser?.name || 'user' }
  });
  
  return true;
}

/**
 * Verify a user's current password
 * Used for self-service password change validation
 */
async function verifyPassword(userId, password) {
  const user = cache.users.find(u => u.id === userId);
  if (!user) {
    return false;
  }
  // Use bcrypt to verify password against stored hash
  return userService.verifyPassword(password, user.password);
}

/**
 * Change own password (self-service)
 * Requires current password verification before allowing change
 */
async function changeOwnPassword(userId, currentPassword, newPassword) {
  // Verify current password first (now async)
  const isValid = await verifyPassword(userId, currentPassword);
  if (!isValid) {
    throw new Error('Current password is incorrect');
  }
  
  // Validate new password
  if (!newPassword || newPassword.length < 8) {
    throw new Error('New password must be at least 8 characters');
  }
  
  const user = cache.users.find(u => u.id === userId);
  
  // Update password in database (supabaseService will hash it)
  const updatedUser = await userService.update(userId, { password: newPassword });
  
  // Update cache with the hashed password from the response
  const index = cache.users.findIndex(u => u.id === userId);
  if (index >= 0) {
    cache.users[index].password = updatedUser.password;
  }
  
  // Log audit (different action than admin reset)
  await addAuditLog({
    action: AUDIT_ACTIONS.PASSWORD_CHANGED,
    userId: userId,
    userName: user?.name,
    targetType: 'user',
    targetId: userId,
    details: { action: 'self-service password change' }
  });
  
  return true;
}

async function updateUserAvailability(id, availability, updatedBy = null) {
  await userService.update(id, { availability });
  const index = cache.users.findIndex(u => u.id === id);
  const targetUser = cache.users[index];
  if (index >= 0) {
    cache.users[index].availability = availability;
  }
  
  // Summarize the availability changes
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  const availableDays = days.filter(d => availability?.[d]?.available).map(d => d.charAt(0).toUpperCase() + d.slice(1, 3));
  const summary = availableDays.length > 0 ? `Available: ${availableDays.join(', ')}` : 'Set as unavailable';
  
  await addAuditLog({
    action: AUDIT_ACTIONS.AVAILABILITY_UPDATED,
    userId: updatedBy?.id || id,
    userName: updatedBy?.name || targetUser?.name,
    targetType: 'user',
    targetId: id,
    details: { 
      targetName: targetUser?.name || 'user',
      changes: summary
    }
  });
  
  return true;
}

async function updateUserDarkMode(id, darkMode) {
  await userService.update(id, { darkMode });
  const index = cache.users.findIndex(u => u.id === id);
  if (index >= 0) {
    cache.users[index].darkMode = darkMode;
  }
  return true;
}

async function updateUserHeaderColor(id, headerColor) {
  await userService.update(id, { headerColor });
  const index = cache.users.findIndex(u => u.id === id);
  if (index >= 0) {
    cache.users[index].headerColor = headerColor;
  }
  return true;
}

// ============================================================================
// AUTHENTICATION METHODS
// ============================================================================

async function authenticate(username, password, clientInfo = {}) {
  const user = cache.users.find(u => u.username === username);
  
  if (!user) {
    await addAuditLog({
      action: AUDIT_ACTIONS.LOGIN_FAILED,
      details: { 
        username, 
        reason: 'User not found',
        ip: clientInfo.ip,
        browser: clientInfo.browser,
        os: clientInfo.os,
        device: clientInfo.device
      }
    });
    return null;
  }
  
  // Use bcrypt to verify password against stored hash
  const isValidPassword = await userService.verifyPassword(password, user.password);
  
  if (!isValidPassword) {
    await addAuditLog({
      action: AUDIT_ACTIONS.LOGIN_FAILED,
      userId: user.id,
      userName: user.name,
      details: { 
        reason: 'Invalid password',
        ip: clientInfo.ip,
        browser: clientInfo.browser,
        os: clientInfo.os,
        device: clientInfo.device
      }
    });
    return null;
  }
  
  await addAuditLog({
    action: AUDIT_ACTIONS.USER_LOGIN,
    userId: user.id,
    userName: user.name,
    details: {
      role: user.role,
      ip: clientInfo.ip,
      browser: clientInfo.browser,
      os: clientInfo.os,
      device: clientInfo.device
    }
  });
  
  // Return user without password
  const { password: _, ...safeUser } = user;
  return safeUser;
}

async function logout(user, clientInfo = {}) {
  if (user) {
    await addAuditLog({
      action: AUDIT_ACTIONS.USER_LOGOUT,
      userId: user.id,
      userName: user.name,
      details: {
        role: user.role,
        ip: clientInfo.ip,
        browser: clientInfo.browser,
        os: clientInfo.os,
        device: clientInfo.device
      }
    });
  }
}


// ============================================================================
// ROOM METHODS
// ============================================================================

function getRooms() {
  return cache.rooms;
}

function getRoomById(id) {
  return cache.rooms.find(r => r.id === id) || null;
}

function getVisibleRooms() {
  return cache.rooms.filter(r => r.isVisible !== false);
}

async function createRoom(roomData, createdBy = null) {
  const newRoom = await roomService.create(roomData);
  cache.rooms.push(newRoom);
  
  await addAuditLog({
    action: AUDIT_ACTIONS.ROOM_CREATED,
    userId: createdBy?.id,
    userName: createdBy?.name,
    targetType: 'room',
    targetId: newRoom.id,
    details: { name: newRoom.name }
  });
  
  return newRoom;
}

async function updateRoom(id, updates, updatedBy = null) {
  const existingRoom = cache.rooms.find(r => r.id === id);
  const room = await roomService.update(id, updates);
  const index = cache.rooms.findIndex(r => r.id === id);
  if (index >= 0) {
    cache.rooms[index] = { ...cache.rooms[index], ...room };
  }
  
  // Helper to normalize values for comparison (handle undefined/null as defaults)
  const normalizeValue = (field, value) => {
    // Boolean fields default to true or false
    if (field === 'showOnStudentSchedule' || field === 'showOnTeacherSchedule') {
      return value === undefined || value === null ? true : value;
    }
    if (field === 'isUtility' || field === 'isVisible') {
      return value === undefined || value === null ? false : value;
    }
    // For objects like ageRange, compare JSON strings
    if (field === 'ageRange' && typeof value === 'object') {
      return JSON.stringify(value);
    }
    return value;
  };
  
  // Filter to only meaningful changes
  const meaningfulFields = ['name', 'shortName', 'capacity', 'ratio', 'minRatio', 'maxRatio', 'ageGroup', 'ageRange', 'isVisible', 'showOnStudentSchedule', 'showOnTeacherSchedule', 'isUtility'];
  const meaningfulUpdates = {};
  let changeDescription = [];
  
  for (const field of meaningfulFields) {
    if (updates[field] !== undefined) {
      const newVal = normalizeValue(field, updates[field]);
      const oldVal = normalizeValue(field, existingRoom?.[field]);
      
      if (newVal !== oldVal) {
        meaningfulUpdates[field] = updates[field];
        if (field === 'name') {
          changeDescription.push(`renamed to "${updates[field]}"`);
        } else if (field === 'shortName') {
          changeDescription.push(`short name changed to "${updates[field]}"`);
        } else if (field === 'capacity') {
          changeDescription.push(`capacity changed to ${updates[field]}`);
        } else if (field === 'ratio') {
          changeDescription.push(`ratio changed to 1:${updates[field]}`);
        } else if (field === 'isVisible') {
          changeDescription.push(updates[field] ? 'made visible' : 'hidden from schedule');
        } else if (field === 'showOnStudentSchedule') {
          changeDescription.push(updates[field] ? 'shown on student schedule' : 'hidden from student schedule');
        } else if (field === 'showOnTeacherSchedule') {
          changeDescription.push(updates[field] ? 'shown on teacher schedule' : 'hidden from teacher schedule');
        } else if (field === 'isUtility') {
          changeDescription.push(updates[field] ? 'marked as utility room' : 'unmarked as utility room');
        } else if (field === 'minRatio' || field === 'maxRatio') {
          changeDescription.push(`ratio updated`);
        } else if (field === 'ageRange' || field === 'ageGroup') {
          changeDescription.push(`age range updated`);
        } else {
          changeDescription.push(`${field} updated`);
        }
      }
    }
  }
  
  // Only log if there were meaningful changes
  if (Object.keys(meaningfulUpdates).length > 0) {
    await addAuditLog({
      action: AUDIT_ACTIONS.ROOM_UPDATED,
      userId: updatedBy?.id,
      userName: updatedBy?.name,
      targetType: 'room',
      targetId: id,
      details: { 
        name: existingRoom?.name || 'Unknown',
        changes: changeDescription.join(', ')
      }
    });
  }
  
  return room;
}

async function deleteRoom(id, deletedBy = null) {
  const room = cache.rooms.find(r => r.id === id);
  await roomService.delete(id);
  cache.rooms = cache.rooms.filter(r => r.id !== id);
  
  await addAuditLog({
    action: AUDIT_ACTIONS.ROOM_DELETED,
    userId: deletedBy?.id,
    userName: deletedBy?.name,
    targetType: 'room',
    targetId: id,
    details: { name: room?.name }
  });
  
  return true;
}

async function updateRoomOrder(roomIds) {
  for (let i = 0; i < roomIds.length; i++) {
    await roomService.update(roomIds[i], { displayOrder: i });
    const index = cache.rooms.findIndex(r => r.id === roomIds[i]);
    if (index >= 0) {
      cache.rooms[index].displayOrder = i;
    }
  }
  // Re-sort cache
  cache.rooms.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  return true;
}

// ============================================================================
// SCHEDULE METHODS
// ============================================================================

function getSchedule() {
  return cache.schedule;
}

function getScheduleForDate(date) {
  return cache.schedule[date] || {};
}

function getScheduleForWeek(startDate) {
  const dates = [];
  const start = new Date(startDate);
  for (let i = 0; i < 5; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    dates.push(d.toISOString().split('T')[0]);
  }
  
  const weekSchedule = {};
  dates.forEach(date => {
    weekSchedule[date] = cache.schedule[date] || {};
  });
  return weekSchedule;
}

/**
 * Get array of date strings (Mon-Fri) for a week given the Monday date
 */
function getWeekDates(mondayDateStr) {
  const monday = new Date(mondayDateStr + 'T12:00:00');
  const dates = [];
  
  for (let i = 0; i < 5; i++) {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    dates.push(`${year}-${month}-${day}`);
  }
  
  return dates;
}

async function addScheduleAssignment(date, roomId, userId, startTime, endTime, addedBy = null) {
  const result = await scheduleService.addAssignment(date, roomId, userId, startTime, endTime);
  
  // Update cache - use 'id' to match what pages expect
  if (!cache.schedule[date]) cache.schedule[date] = {};
  if (!cache.schedule[date][roomId]) cache.schedule[date][roomId] = [];
  cache.schedule[date][roomId].push({
    id: result.id,
    odId: result.id,  // Keep for backward compatibility
    userId: userId,
    startTime,
    endTime
  });
  
  const user = cache.users.find(u => u.id === userId);
  const room = cache.rooms.find(r => r.id === roomId);
  await addAuditLog({
    action: AUDIT_ACTIONS.SCHEDULE_ASSIGNMENT_ADDED,
    userId: addedBy?.id,
    userName: addedBy?.name,
    targetType: 'schedule',
    details: { 
      date, 
      room: room?.name || 'Unknown Room', 
      teacher: user?.name || 'Unknown Teacher', 
      time: `${startTime} - ${endTime}`
    }
  });
  
  return result;
}

async function removeScheduleAssignment(date, roomId, assignmentId, removedBy = null) {
  // Get assignment details before removing
  const room = cache.rooms.find(r => r.id === roomId);
  let assignedUser = null;
  if (cache.schedule[date] && cache.schedule[date][roomId]) {
    const assignment = cache.schedule[date][roomId].find(a => a.id === assignmentId || a.odId === assignmentId);
    if (assignment) {
      assignedUser = cache.users.find(u => u.id === assignment.userId);
    }
  }
  
  await scheduleService.removeAssignment(assignmentId);
  
  // Update cache - filter by both id and odId for compatibility
  if (cache.schedule[date] && cache.schedule[date][roomId]) {
    cache.schedule[date][roomId] = cache.schedule[date][roomId].filter(a => 
      a.id !== assignmentId && a.odId !== assignmentId
    );
    if (cache.schedule[date][roomId].length === 0) {
      delete cache.schedule[date][roomId];
    }
  }
  
  await addAuditLog({
    action: AUDIT_ACTIONS.SCHEDULE_ASSIGNMENT_REMOVED,
    userId: removedBy?.id,
    userName: removedBy?.name,
    targetType: 'schedule',
    details: { 
      date, 
      room: room?.name || 'Unknown Room', 
      teacher: assignedUser?.name || 'Unknown Teacher'
    }
  });
  
  return true;
}

async function clearScheduleForDay(date, clearedBy = null) {
  await scheduleService.clearDay(date);
  delete cache.schedule[date];
  
  await addAuditLog({
    action: AUDIT_ACTIONS.SCHEDULE_CLEARED,
    userId: clearedBy?.id,
    userName: clearedBy?.name,
    targetType: 'schedule',
    details: { date }
  });
  
  return true;
}

// Refresh schedule from database (useful after bulk operations)
async function refreshSchedule() {
  cache.schedule = await scheduleService.getAll();
  return cache.schedule;
}

// ============================================================================
// SCHEDULE PUBLISH STATUS METHODS
// ============================================================================

/**
 * Check if a week is published (visible to teachers)
 */
function isWeekPublished(weekStart) {
  return cache.publishedWeeks[weekStart] === true;
}

/**
 * Set publish status for a week
 */
async function setWeekPublished(weekStart, isPublished, actor = null) {
  await schedulePublishService.setPublishStatus(weekStart, isPublished, actor?.id);
  cache.publishedWeeks[weekStart] = isPublished;
  
  await addAuditLog({
    action: isPublished ? AUDIT_ACTIONS.SCHEDULE_PUBLISHED : AUDIT_ACTIONS.SCHEDULE_UNPUBLISHED,
    userId: actor?.id,
    userName: actor?.name,
    targetType: 'schedule',
    details: { weekStart, status: isPublished ? 'Published' : 'Unpublished' }
  });
  
  return isPublished;
}

/**
 * Get all published weeks
 */
function getPublishedWeeks() {
  return cache.publishedWeeks;
}


// ============================================================================
// PTO METHODS
// ============================================================================

function getPTORequests() {
  return cache.ptoRequests;
}

function getPTORequestsByUser(userId) {
  return cache.ptoRequests.filter(p => p.userId === userId);
}

function getPTORequestById(id) {
  return cache.ptoRequests.find(p => p.id === id) || null;
}

function getPendingPTORequests() {
  return cache.ptoRequests.filter(p => p.status === 'Pending');
}

function getApprovedPTOForDate(date) {
  return cache.ptoRequests.filter(p => 
    p.status === 'Approved' && p.dates.includes(date)
  );
}

function getPTORequestsByStatus(status) {
  return cache.ptoRequests.filter(p => p.status === status);
}

async function createPTORequest(userIdOrData, datesOrCreatedBy = null, reason = '', partialDayInfo = null) {
  // Handle both calling conventions:
  // 1. createPTORequest({ userId, dates, reason, ... }, createdBy)  - object format
  // 2. createPTORequest(userId, dates, reason, partialDayInfo)       - separate args format
  
  let requestData;
  let createdBy = null;
  
  if (typeof userIdOrData === 'object' && userIdOrData !== null && !Array.isArray(userIdOrData)) {
    // Object format: createPTORequest(requestData, createdBy)
    requestData = userIdOrData;
    createdBy = datesOrCreatedBy;
  } else {
    // Separate args format: createPTORequest(userId, dates, reason, partialDayInfo)
    // partialDayInfo comes from PTORequest.js as: { isPartial, startTime, hours }
    requestData = {
      userId: userIdOrData,
      dates: datesOrCreatedBy,
      reason: reason,
      isPartialDay: partialDayInfo?.isPartial || false,
      partialStartTime: partialDayInfo?.startTime || null,
      partialHours: partialDayInfo?.hours || null
    };
  }
  
  // Server-side validation: Enforce 2-week advance notice requirement (only for non-admin users)
  // This is a safety net in case client-side validation is bypassed
  const requestingUser = cache.users.find(u => u.id === requestData.userId);
  
  if (requestingUser?.role !== 'ADMIN' && requestingUser?.role !== 'SUPER_ADMIN') {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const twoWeeksFromNow = new Date(today);
    twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 14);
    
    const tooSoonDates = (requestData.dates || []).filter(dateStr => {
      const requestDate = new Date(dateStr + 'T12:00:00');
      return requestDate < twoWeeksFromNow;
    });
    
    if (tooSoonDates.length > 0) {
      const formattedDates = tooSoonDates.map(d => {
        const [year, month, day] = d.split('-');
        return `${month}/${day}/${year}`;
      }).join(', ');
      throw new Error(`PTO requests must be submitted at least 2 weeks in advance. The following dates are too soon: ${formattedDates}`);
    }
  }
  
  const newRequest = await ptoService.create(requestData);
  cache.ptoRequests.unshift(newRequest);
  
  await addAuditLog({
    action: AUDIT_ACTIONS.PTO_REQUESTED,
    userId: createdBy?.id || requestData.userId,
    userName: createdBy?.name || requestingUser?.name,
    targetType: 'pto',
    details: { 
      requestedBy: requestingUser?.name || 'Unknown',
      dates: requestData.dates?.join(', ') || 'No dates', 
      reason: requestData.reason || 'No reason provided',
      partialDay: requestData.isPartialDay ? `${requestData.partialHours}h starting ${requestData.partialStartTime}` : null
    }
  });
  
  return newRequest;
}

async function approvePTORequest(id, approvedBy) {
  await ptoService.updateStatus(id, 'Approved', approvedBy.id);
  const index = cache.ptoRequests.findIndex(p => p.id === id);
  if (index >= 0) {
    cache.ptoRequests[index].status = 'Approved';
    cache.ptoRequests[index].reviewedBy = approvedBy.id;
    cache.ptoRequests[index].reviewedAt = new Date().toISOString();
  }
  
  const request = cache.ptoRequests[index];
  const user = cache.users.find(u => u.id === request?.userId);
  await addAuditLog({
    action: AUDIT_ACTIONS.PTO_APPROVED,
    userId: approvedBy.id,
    userName: approvedBy.name,
    targetType: 'pto',
    details: { 
      teacher: user?.name || 'Unknown', 
      dates: request?.dates?.join(', ') || 'No dates'
    }
  });
  
  return true;
}

async function denyPTORequest(id, deniedBy) {
  await ptoService.updateStatus(id, 'Denied', deniedBy.id);
  const index = cache.ptoRequests.findIndex(p => p.id === id);
  if (index >= 0) {
    cache.ptoRequests[index].status = 'Denied';
    cache.ptoRequests[index].reviewedBy = deniedBy.id;
    cache.ptoRequests[index].reviewedAt = new Date().toISOString();
  }
  
  const request = cache.ptoRequests[index];
  const user = cache.users.find(u => u.id === request?.userId);
  await addAuditLog({
    action: AUDIT_ACTIONS.PTO_DENIED,
    userId: deniedBy.id,
    userName: deniedBy.name,
    targetType: 'pto',
    details: { 
      teacher: user?.name || 'Unknown', 
      dates: request?.dates?.join(', ') || 'No dates'
    }
  });
  
  return true;
}

async function cancelPTORequest(id, cancelledByOrId) {
  const request = cache.ptoRequests.find(p => p.id === id);
  const requestUser = cache.users.find(u => u.id === request?.userId);
  
  // Handle both: cancelledBy as object {id, name} or as just an ID string
  let cancelledBy;
  if (typeof cancelledByOrId === 'object' && cancelledByOrId !== null) {
    cancelledBy = cancelledByOrId;
  } else {
    // It's a user ID - look up the user
    const foundUser = cache.users.find(u => u.id === cancelledByOrId);
    cancelledBy = foundUser ? { id: foundUser.id, name: foundUser.name } : { id: cancelledByOrId, name: null };
  }
  
  await ptoService.delete(id);
  cache.ptoRequests = cache.ptoRequests.filter(p => p.id !== id);
  
  await addAuditLog({
    action: AUDIT_ACTIONS.PTO_CANCELLED,
    userId: cancelledBy?.id,
    userName: cancelledBy?.name,
    targetType: 'pto',
    details: { 
      teacher: requestUser?.name || 'Unknown',
      dates: request?.dates?.join(', ') || 'No dates'
    }
  });
  
  return true;
}

// ============================================================================
// CHILDREN METHODS
// ============================================================================

function getChildren() {
  return cache.children;
}

function getChildById(id) {
  return cache.children.find(c => c.id === id) || null;
}

function getActiveChildren() {
  return cache.children.filter(c => c.status?.toLowerCase() === 'active');
}

function getChildrenByRoom(roomId) {
  return cache.children.filter(c => c.primaryRoomId === roomId);
}

async function createChild(childData, createdBy = null) {
  // Transform data from page format to service format
  const transformedData = {
    ...childData,
    // Handle parentContact object -> separate fields
    parentName: childData.parentContact?.name || childData.parentName || null,
    parentPhone: childData.parentContact?.phone || childData.parentPhone || null,
    parentEmail: childData.parentContact?.email || childData.parentEmail || null,
    // Handle customHours object -> separate fields
    useCustomHours: childData.customHours ? true : (childData.useCustomHours || false),
    customHoursStart: childData.customHours?.start || childData.customHoursStart || '08:00',
    customHoursEnd: childData.customHours?.end || childData.customHoursEnd || '17:00'
  };
  
  const newChild = await childService.create(transformedData);
  cache.children.push(newChild);
  
  await addAuditLog({
    action: AUDIT_ACTIONS.CHILD_CREATED,
    userId: createdBy?.id,
    userName: createdBy?.name,
    targetType: 'child',
    targetId: newChild.id,
    details: { name: `${childData.firstName} ${childData.lastName}` }
  });
  
  return newChild;
}

async function updateChild(id, updates, updatedBy = null) {
  const existingChild = cache.children.find(c => c.id === id);
  // Transform data from page format to service format
  // Handle customHours: when null, explicitly set useCustomHours to false
  const hasCustomHoursKey = 'customHours' in updates;
  const transformedUpdates = {
    ...updates,
    parentName: updates.parentContact?.name || updates.parentName,
    parentPhone: updates.parentContact?.phone || updates.parentPhone,
    parentEmail: updates.parentContact?.email || updates.parentEmail,
    useCustomHours: hasCustomHoursKey ? (updates.customHours ? true : false) : updates.useCustomHours,
    customHoursStart: updates.customHours?.start || updates.customHoursStart,
    customHoursEnd: updates.customHours?.end || updates.customHoursEnd
  };
  
  const child = await childService.update(id, transformedUpdates);
  const index = cache.children.findIndex(c => c.id === id);
  if (index >= 0) {
    cache.children[index] = { ...cache.children[index], ...child };
  }
  
  // Build meaningful change description with before/after values
  let changeDescription = [];
  
  if (updates.firstName !== undefined && updates.firstName !== existingChild?.firstName) {
    changeDescription.push(`first name: "${existingChild?.firstName || ''}" → "${updates.firstName}"`);
  }
  if (updates.lastName !== undefined && updates.lastName !== existingChild?.lastName) {
    changeDescription.push(`last name: "${existingChild?.lastName || ''}" → "${updates.lastName}"`);
  }
  if (updates.dateOfBirth !== undefined && updates.dateOfBirth !== existingChild?.dateOfBirth) {
    changeDescription.push(`DOB: "${existingChild?.dateOfBirth || ''}" → "${updates.dateOfBirth}"`);
  }
  if (updates.primaryRoomId !== undefined && updates.primaryRoomId !== existingChild?.primaryRoomId) {
    const oldRoom = cache.rooms.find(r => r.id === existingChild?.primaryRoomId);
    const newRoom = cache.rooms.find(r => r.id === updates.primaryRoomId);
    changeDescription.push(`room: "${oldRoom?.name || 'None'}" → "${newRoom?.name || 'None'}"`);
  }
  if (updates.status !== undefined && updates.status !== existingChild?.status) {
    changeDescription.push(`status: "${existingChild?.status || ''}" → "${updates.status}"`);
  }
  if (updates.notes !== undefined && updates.notes !== existingChild?.notes) {
    changeDescription.push(`notes updated`);
  }
  if (updates.scheduledDays !== undefined && JSON.stringify(updates.scheduledDays) !== JSON.stringify(existingChild?.scheduledDays)) {
    changeDescription.push(`scheduled days: "${existingChild?.scheduledDays?.join(', ') || ''}" → "${updates.scheduledDays?.join(', ') || ''}"`);
  }
  
  const childName = `${existingChild?.firstName || ''} ${existingChild?.lastName || ''}`.trim() || 'Unknown';
  
  await addAuditLog({
    action: AUDIT_ACTIONS.CHILD_UPDATED,
    userId: updatedBy?.id,
    userName: updatedBy?.name,
    targetType: 'child',
    targetId: id,
    details: { 
      name: childName,
      changes: changeDescription.length > 0 ? changeDescription.join(', ') : 'record updated'
    }
  });
  
  return child;
}

async function deleteChild(id, hardDelete = false, deletedBy = null) {
  const child = cache.children.find(c => c.id === id);
  
  if (hardDelete) {
    // Permanent delete
    await childService.delete(id);
    cache.children = cache.children.filter(c => c.id !== id);
    
    await addAuditLog({
      action: AUDIT_ACTIONS.CHILD_DELETED,
      userId: deletedBy?.id,
      userName: deletedBy?.name,
      targetType: 'child',
      targetId: id,
      details: { name: `${child?.firstName} ${child?.lastName}` }
    });
  } else {
    // Soft delete - just set status to inactive
    await childService.update(id, { status: 'inactive' });
    const index = cache.children.findIndex(c => c.id === id);
    if (index >= 0) {
      cache.children[index].status = 'inactive';
    }
    
    await addAuditLog({
      action: AUDIT_ACTIONS.CHILD_UPDATED,
      userId: deletedBy?.id,
      userName: deletedBy?.name,
      targetType: 'child',
      targetId: id,
      details: { name: `${child?.firstName} ${child?.lastName}`, action: 'deactivated' }
    });
  }
  
  return true;
}


// ============================================================================
// CHILD SCHEDULE METHODS
// ============================================================================

function getChildSchedule() {
  return cache.childSchedule;
}

function getChildScheduleForDate(date) {
  return cache.childSchedule[date] || {};
}

/**
 * Get child schedule for a week (Monday-Friday)
 */
function getChildScheduleForWeek(mondayDateStr) {
  const schedule = cache.childSchedule;
  const weekSchedule = {};
  
  const monday = new Date(mondayDateStr + 'T12:00:00');
  for (let i = 0; i < 5; i++) {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    const dateStr = date.toISOString().split('T')[0];
    weekSchedule[dateStr] = schedule[dateStr] || {};
  }
  
  return weekSchedule;
}

/**
 * Get children in a specific room on a specific date
 */
function getChildrenInRoom(roomId, dateStr) {
  const daySchedule = getChildScheduleForDate(dateStr);
  const result = [];
  
  for (const childId in daySchedule) {
    const entry = daySchedule[childId];
    if (entry.roomId === roomId) {
      const child = cache.children.find(c => c.id === childId);
      if (child && child.status === 'active') {
        result.push({ child, scheduleEntry: entry });
      }
    }
  }
  
  return result;
}

/**
 * Get a specific child's schedule entry for a date
 */
function getChildDaySchedule(childId, dateStr) {
  const daySchedule = getChildScheduleForDate(dateStr);
  return daySchedule[childId] || null;
}

/**
 * Assign a child to a room for a specific date
 */
async function assignChildToRoom(childId, roomId, dateStr, options = {}, actor = null) {
  const child = cache.children.find(c => c.id === childId);
  const room = cache.rooms.find(r => r.id === roomId);
  
  if (!child) throw new Error('Child not found');
  if (!room) throw new Error('Room not found');
  
  if (!cache.childSchedule[dateStr]) {
    cache.childSchedule[dateStr] = {};
  }
  
  cache.childSchedule[dateStr][childId] = {
    roomId,
    startTime: options.startTime || null,
    endTime: options.endTime || null,
    isVisit: options.isVisit || false,
    visitNote: options.visitNote || '',
    isAbsent: options.isAbsent || false
  };
  
  // Save to Supabase with options
  await childScheduleService.addChild(dateStr, roomId, childId, options);
  
  // Use CHILD_VISIT_SCHEDULED for visits, CHILD_SCHEDULED for regular assignments
  await addAuditLog({
    action: options.isVisit ? AUDIT_ACTIONS.CHILD_VISIT_SCHEDULED : AUDIT_ACTIONS.CHILD_SCHEDULED,
    userId: actor?.id,
    userName: actor?.name,
    targetType: 'child_schedule',
    details: { 
      student: `${child.firstName} ${child.lastName}`, 
      room: room.name, 
      date: dateStr,
      time: options.startTime && options.endTime ? `${options.startTime} - ${options.endTime}` : 'Full day',
      type: options.isVisit ? 'Visit' : 'Regular'
    }
  });
  
  return true;
}

/**
 * Toggle absent status for a child on a specific date
 */
async function toggleChildAbsent(childId, dateStr, actor = null) {
  const child = cache.children.find(c => c.id === childId);
  if (!child) throw new Error('Child not found');
  
  const entry = cache.childSchedule[dateStr]?.[childId];
  if (!entry) throw new Error('Child not scheduled for this date');
  
  const newAbsentStatus = !entry.isAbsent;
  
  // Update cache
  cache.childSchedule[dateStr][childId].isAbsent = newAbsentStatus;
  
  // Save to Supabase
  await childScheduleService.updateChildAbsent(dateStr, childId, newAbsentStatus);
  
  // Audit log
  await addAuditLog({
    action: newAbsentStatus ? AUDIT_ACTIONS.CHILD_MARKED_ABSENT : AUDIT_ACTIONS.CHILD_MARKED_PRESENT,
    userId: actor?.id,
    userName: actor?.name,
    targetType: 'child_schedule',
    details: { 
      student: `${child.firstName} ${child.lastName}`, 
      date: dateStr,
      status: newAbsentStatus ? 'Absent' : 'Present'
    }
  });
  
  return newAbsentStatus;
}

/**
 * Get day code from date string (M, T, W, TH, F)
 */
function getDayCode(dateStr) {
  const date = new Date(dateStr + 'T12:00:00');
  const dayIndex = date.getDay();
  const dayCodes = ['', 'M', 'T', 'W', 'TH', 'F', ''];
  return dayCodes[dayIndex] || '';
}

/**
 * Apply default schedule for children based on their enrollment
 * OPTIMIZED: Uses batch insert instead of individual database calls
 */
async function applyDefaultSchedule(childIds = null, startDateStr, endDateStr, overwrite = false, actor = null) {
  const children = childIds 
    ? childIds.map(id => cache.children.find(c => c.id === id)).filter(Boolean)
    : cache.children.filter(c => c.status?.toLowerCase() === 'active');
  
  let added = 0;
  let skipped = 0;
  const entriesToInsert = [];
  
  const startDate = new Date(startDateStr + 'T12:00:00');
  const endDate = new Date(endDateStr + 'T12:00:00');
  
  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    const dateStr = d.toISOString().split('T')[0];
    const dayCode = getDayCode(dateStr);
    
    // Skip weekends
    if (!dayCode) continue;
    
    if (!cache.childSchedule[dateStr]) {
      cache.childSchedule[dateStr] = {};
    }
    
    for (const child of children) {
      // Check if child is scheduled for this day of week
      if (!child.scheduledDays || !child.scheduledDays.includes(dayCode)) continue;
      
      // Check if already scheduled
      if (cache.childSchedule[dateStr][child.id] && !overwrite) {
        skipped++;
        continue;
      }
      
      const entry = {
        roomId: child.primaryRoomId,
        startTime: child.customHoursStart || '08:00',
        endTime: child.customHoursEnd || '17:00',
        isVisit: false,
        visitNote: null
      };
      
      // Add to cache immediately
      cache.childSchedule[dateStr][child.id] = entry;
      
      // Queue for batch insert
      entriesToInsert.push({
        date: dateStr,
        roomId: child.primaryRoomId,
        childId: child.id,
        isVisit: false,
        visitNote: null,
        startTime: child.customHoursStart || '08:00',
        endTime: child.customHoursEnd || '17:00'
      });
      added++;
    }
  }
  
  // Batch insert all entries in one database call
  if (entriesToInsert.length > 0) {
    // Log what we're about to insert for debugging
    const datesSummary = {};
    entriesToInsert.forEach(e => {
      datesSummary[e.date] = (datesSummary[e.date] || 0) + 1;
    });
    console.log('applyDefaultSchedule: Entries to insert by date:', datesSummary);
    
    const result = await childScheduleService.addChildrenBulk(entriesToInsert);
    console.log('applyDefaultSchedule: Bulk insert result:', result);
    if (result.errors.length > 0) {
      console.error('Batch insert errors:', result.errors);
    }
  }
  
  // Refresh cache from database to ensure sync after bulk operation
  await refreshChildScheduleForDateRange(startDateStr, endDateStr);
  
  await addAuditLog({
    action: AUDIT_ACTIONS.CHILD_SCHEDULED,
    userId: actor?.id,
    userName: actor?.name,
    targetType: 'child_schedule',
    details: {
      action: 'Bulk schedule applied',
      dateRange: `${startDateStr} to ${endDateStr}`,
      studentsProcessed: children.length,
      added: added,
      skipped: skipped
    }
  });
  
  return { added, skipped, total: children.length };
}

async function addChildToSchedule(date, roomId, childId, addedBy = null) {
  await childScheduleService.addChild(date, roomId, childId);
  
  if (!cache.childSchedule[date]) cache.childSchedule[date] = {};
  cache.childSchedule[date][childId] = {
    roomId: roomId,
    startTime: null,
    endTime: null,
    isVisit: false,
    visitNote: null
  };
  
  const child = cache.children.find(c => c.id === childId);
  const room = cache.rooms.find(r => r.id === roomId);
  await addAuditLog({
    action: AUDIT_ACTIONS.CHILD_SCHEDULED,
    userId: addedBy?.id,
    userName: addedBy?.name,
    targetType: 'child_schedule',
    details: { 
      student: `${child?.firstName || ''} ${child?.lastName || ''}`.trim() || 'Unknown',
      room: room?.name || 'Unknown Room',
      date: date
    }
  });
  
  return true;
}

async function removeChildFromSchedule(date, childId, removedBy = null) {
  // Get info before removing
  const scheduleEntry = cache.childSchedule[date]?.[childId];
  const room = scheduleEntry ? cache.rooms.find(r => r.id === scheduleEntry.roomId) : null;
  
  await childScheduleService.removeChild(date, childId);
  
  // Remove from cache
  if (cache.childSchedule[date] && cache.childSchedule[date][childId]) {
    delete cache.childSchedule[date][childId];
  }
  
  const child = cache.children.find(c => c.id === childId);
  await addAuditLog({
    action: AUDIT_ACTIONS.CHILD_UNSCHEDULED,
    userId: removedBy?.id,
    userName: removedBy?.name,
    targetType: 'child_schedule',
    details: { 
      student: `${child?.firstName || ''} ${child?.lastName || ''}`.trim() || 'Unknown',
      room: room?.name || 'Unknown Room',
      date: date
    }
  });
  
  return true;
}

// Alias with swapped argument order (childId, date) for page compatibility
async function removeChildFromDay(childId, date, removedBy = null) {
  return removeChildFromSchedule(date, childId, removedBy);
}

/**
 * Clear all child schedule assignments for a specific day
 * OPTIMIZED: Uses single database call instead of per-child calls
 */
async function clearChildScheduleForDay(date, clearedBy = null) {
  const daySchedule = cache.childSchedule[date] || {};
  const childIds = Object.keys(daySchedule);
  
  // Single database call to delete all entries for this date
  if (childIds.length > 0) {
    await childScheduleService.deleteByDate(date);
  }
  
  // Clear cache for this day
  delete cache.childSchedule[date];
  
  // Single audit log entry for the bulk clear
  if (childIds.length > 0) {
    await addAuditLog({
      action: AUDIT_ACTIONS.CHILD_SCHEDULE_CLEARED,
      userId: clearedBy?.id,
      userName: clearedBy?.name,
      targetType: 'child_schedule',
      details: { 
        date: date,
        studentsCleared: childIds.length
      }
    });
  }
  
  return { cleared: childIds.length };
}

/**
 * Refresh child schedule cache from database for a date range
 * Call this after bulk operations to ensure cache is in sync with database
 */
async function refreshChildScheduleForDateRange(startDate, endDate) {
  try {
    console.log('refreshChildScheduleForDateRange: Fetching from database for', startDate, 'to', endDate);
    const freshSchedule = await childScheduleService.getByDateRange(startDate, endDate);
    
    // Log what we got from the database
    const datesSummary = {};
    Object.keys(freshSchedule).forEach(date => {
      datesSummary[date] = Object.keys(freshSchedule[date]).length;
    });
    console.log('refreshChildScheduleForDateRange: Database returned entries by date:', datesSummary);
    
    // Clear existing cache entries for this range first
    const start = new Date(startDate + 'T12:00:00');
    const end = new Date(endDate + 'T12:00:00');
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      // Replace with fresh data from database (or empty object if no entries)
      cache.childSchedule[dateStr] = freshSchedule[dateStr] || {};
    }
    
    console.log('refreshChildScheduleForDateRange: Cache updated successfully');
    return true;
  } catch (error) {
    console.error('Error refreshing child schedule cache:', error);
    return false;
  }
}

/**
 * Clear all child schedule assignments for a date range (e.g., entire week)
 * OPTIMIZED: Uses single database call for entire range
 * Refreshes cache from database after completion to ensure sync
 */
async function clearChildScheduleForDateRange(startDate, endDate, clearedBy = null) {
  // Count how many entries we're clearing (for audit log)
  let totalCleared = 0;
  const start = new Date(startDate + 'T12:00:00');
  const end = new Date(endDate + 'T12:00:00');
  
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const dateStr = d.toISOString().split('T')[0];
    const daySchedule = cache.childSchedule[dateStr] || {};
    totalCleared += Object.keys(daySchedule).length;
    // Clear cache for this day
    delete cache.childSchedule[dateStr];
  }
  
  // Single database call to delete all entries in range
  if (totalCleared > 0) {
    await childScheduleService.deleteByDateRange(startDate, endDate);
    
    await addAuditLog({
      action: AUDIT_ACTIONS.CHILD_SCHEDULE_CLEARED,
      userId: clearedBy?.id,
      userName: clearedBy?.name,
      targetType: 'child_schedule',
      details: { 
        dateRange: `${startDate} to ${endDate}`,
        studentsCleared: totalCleared
      }
    });
  }
  
  // Refresh cache from database to ensure sync
  await refreshChildScheduleForDateRange(startDate, endDate);
  
  return { cleared: totalCleared };
}

async function moveChildToRoom(date, childId, newRoomId, movedBy = null) {
  await childScheduleService.moveChild(date, childId, newRoomId);
  
  // Update cache - keep the same structure with new roomId
  if (cache.childSchedule[date] && cache.childSchedule[date][childId]) {
    cache.childSchedule[date][childId].roomId = newRoomId;
  } else {
    if (!cache.childSchedule[date]) cache.childSchedule[date] = {};
    cache.childSchedule[date][childId] = { roomId: newRoomId };
  }
  
  return true;
}

function getChildrenScheduledForDate(date) {
  const scheduled = [];
  const daySchedule = cache.childSchedule[date] || {};
  Object.keys(daySchedule).forEach(childId => {
    const entry = daySchedule[childId];
    if (entry && entry.roomId) {
      scheduled.push({ childId, roomId: entry.roomId });
    }
  });
  return scheduled;
}

// ============================================================================
// NOTES METHODS
// ============================================================================

function getDailyNotes() {
  return cache.dailyNotes;
}

function getDailyNote(date) {
  return cache.dailyNotes[date] || null;
}

async function setDailyNote(date, note, type = 'daily', user = null) {
  await notesService.setDailyNote(date, note, type);
  cache.dailyNotes[date] = { note, type };
  return true;
}

function getRoomDayNotes(roomIdOrAll, dateStr) {
  // If called with no params or just one param, return all notes (original behavior)
  if (!dateStr) {
    return cache.roomDayNotes;
  }
  
  // If called with roomId and dateStr, return array of notes for that room/date
  // Convert single note to array format for compatibility
  const note = cache.roomDayNotes[dateStr]?.[roomIdOrAll];
  if (!note) return [];
  
  // If it's already an array (new format), return it
  if (Array.isArray(note)) return note;
  
  // Convert string to array format with generated ID
  return [{
    id: `${dateStr}_${roomIdOrAll}_note`,
    text: note,
    createdByName: 'System',
    createdAt: new Date().toISOString()
  }];
}

function getRoomDayNote(date, roomId) {
  return cache.roomDayNotes[date]?.[roomId] || '';
}

async function setRoomDayNote(date, roomId, note) {
  await notesService.setRoomDayNote(date, roomId, note);
  if (!cache.roomDayNotes[date]) cache.roomDayNotes[date] = {};
  if (note) {
    cache.roomDayNotes[date][roomId] = note;
  } else {
    delete cache.roomDayNotes[date][roomId];
  }
  return true;
}

/**
 * Add a room note (stores as array of notes)
 */
async function addRoomDayNote(roomId, dateStr, text, actor = null) {
  if (!cache.roomDayNotes[dateStr]) cache.roomDayNotes[dateStr] = {};
  
  const currentNotes = cache.roomDayNotes[dateStr][roomId];
  const newNote = {
    id: `note_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    text: text,
    createdByName: actor?.name || 'Unknown',
    createdAt: new Date().toISOString()
  };
  
  // Convert to array if needed and add new note
  if (Array.isArray(currentNotes)) {
    currentNotes.push(newNote);
  } else if (currentNotes) {
    // Convert existing string note to array and add new
    cache.roomDayNotes[dateStr][roomId] = [
      { id: `${dateStr}_${roomId}_old`, text: currentNotes, createdByName: 'System', createdAt: new Date().toISOString() },
      newNote
    ];
  } else {
    cache.roomDayNotes[dateStr][roomId] = [newNote];
  }
  
  // Save to Supabase (as concatenated string for now)
  const allNotes = cache.roomDayNotes[dateStr][roomId];
  const combinedText = Array.isArray(allNotes) 
    ? allNotes.map(n => n.text).join('\n---\n') 
    : allNotes;
  await notesService.setRoomDayNote(dateStr, roomId, combinedText);
  
  return newNote;
}

/**
 * Delete a room note by ID
 */
async function deleteRoomDayNote(roomId, dateStr, noteId, actor = null) {
  if (!cache.roomDayNotes[dateStr]?.[roomId]) return false;
  
  const currentNotes = cache.roomDayNotes[dateStr][roomId];
  
  if (Array.isArray(currentNotes)) {
    cache.roomDayNotes[dateStr][roomId] = currentNotes.filter(n => n.id !== noteId);
    
    // Save to Supabase
    const remaining = cache.roomDayNotes[dateStr][roomId];
    if (remaining.length === 0) {
      await notesService.setRoomDayNote(dateStr, roomId, null);
    } else {
      const combinedText = remaining.map(n => n.text).join('\n---\n');
      await notesService.setRoomDayNote(dateStr, roomId, combinedText);
    }
  } else {
    // Single note - just delete it
    delete cache.roomDayNotes[dateStr][roomId];
    await notesService.setRoomDayNote(dateStr, roomId, null);
  }
  
  return true;
}

// ============================================================================
// AUDIT LOG METHODS
// ============================================================================

/**
 * Helper to convert YYYY-MM-DD to MM/DD/YYYY
 */
function formatDateForDisplay(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return dateStr;
  // Convert any YYYY-MM-DD patterns to MM/DD/YYYY
  return dateStr.replace(/(\d{4})-(\d{2})-(\d{2})/g, '$2/$3/$1');
}

/**
 * Generate human-readable description for audit log entry
 */
function generateAuditDescription(entry) {
  const actor = entry.userName || 'System';
  const details = entry.details || {};
  
  // Format any date fields
  const date = formatDateForDisplay(details.date);
  const dates = formatDateForDisplay(details.dates);
  const dateRange = formatDateForDisplay(details.dateRange);
  
  switch (entry.action) {
    // Authentication
    case AUDIT_ACTIONS.USER_LOGIN: {
      const location = details.ip ? ` from ${details.ip}` : '';
      const device = details.device && details.browser ? ` (${details.browser} on ${details.os})` : '';
      return `${actor} logged in${location}${device}`;
    }
    case AUDIT_ACTIONS.USER_LOGOUT: {
      const location = details.ip ? ` from ${details.ip}` : '';
      return `${actor} logged out${location}`;
    }
    case AUDIT_ACTIONS.LOGIN_FAILED: {
      const target = details.username ? ` for "${details.username}"` : '';
      const location = details.ip ? ` from ${details.ip}` : '';
      const reason = details.reason ? ` - ${details.reason}` : '';
      return `Failed login attempt${target}${location}${reason}`;
    }
    
    // User management
    case AUDIT_ACTIONS.USER_CREATED:
      return `${actor} created user "${details.name || details.username || 'unknown'}" (${details.role === 'SUPER_ADMIN' ? 'Super Admin' : details.role === 'ADMIN' ? 'Administrator' : 'Teacher'})`;
    case AUDIT_ACTIONS.USER_UPDATED: {
      const target = details.targetName || details.name || 'user';
      const changes = details.changes || 'profile';
      return `${actor} updated ${target}: ${changes}`;
    }
    case AUDIT_ACTIONS.USER_DELETED:
      return `${actor} deleted user "${details.name || details.username || 'unknown'}"`;
    case AUDIT_ACTIONS.PASSWORD_RESET: {
      const target = details.targetName || 'user';
      return `${actor} reset password for ${target}`;
    }
    case AUDIT_ACTIONS.PASSWORD_CHANGED:
      return `${actor} changed their password`;
    case AUDIT_ACTIONS.AVAILABILITY_UPDATED: {
      const target = details.targetName || 'user';
      const changes = details.changes || 'availability';
      return `${actor} updated availability for ${target}: ${changes}`;
    }
    
    // Room management
    case AUDIT_ACTIONS.ROOM_CREATED:
      return `${actor} created room "${details.name || 'unknown'}"`;
    case AUDIT_ACTIONS.ROOM_UPDATED: {
      const changes = details.changes || 'settings';
      return `${actor} updated room "${details.name || 'unknown'}": ${changes}`;
    }
    case AUDIT_ACTIONS.ROOM_DELETED:
      return `${actor} deleted room "${details.name || 'unknown'}"`;
    
    // Schedule management
    case AUDIT_ACTIONS.SCHEDULE_ASSIGNMENT_ADDED: {
      const time = details.time ? ` (${details.time})` : '';
      return `${actor} assigned ${details.teacher || 'teacher'} to ${details.room || 'room'} on ${date}${time}`;
    }
    case AUDIT_ACTIONS.SCHEDULE_ASSIGNMENT_REMOVED:
      return `${actor} removed ${details.teacher || 'teacher'} from ${details.room || 'room'} on ${date}`;
    case AUDIT_ACTIONS.SCHEDULE_CLEARED:
      return `${actor} cleared all assignments for ${date}`;
    
    // PTO management
    case AUDIT_ACTIONS.PTO_REQUESTED: {
      const requestor = details.requestedBy || actor;
      const dateStr = dates ? `: ${dates}` : '';
      const reason = details.reason ? ` - ${details.reason}` : '';
      return `${requestor} submitted PTO request${dateStr}${reason}`;
    }
    case AUDIT_ACTIONS.PTO_APPROVED: {
      const dateStr = dates ? ` (${dates})` : '';
      return `${actor} approved PTO for ${details.teacher || 'teacher'}${dateStr}`;
    }
    case AUDIT_ACTIONS.PTO_DENIED: {
      const dateStr = dates ? ` (${dates})` : '';
      return `${actor} denied PTO for ${details.teacher || 'teacher'}${dateStr}`;
    }
    case AUDIT_ACTIONS.PTO_CANCELLED: {
      const dateStr = dates ? ` (${dates})` : '';
      return `${actor} cancelled PTO request for ${details.teacher || 'teacher'}${dateStr}`;
    }
    
    // Child/Student management
    case AUDIT_ACTIONS.CHILD_CREATED:
      return `${actor} enrolled student "${details.name || 'unknown'}"`;
    case AUDIT_ACTIONS.CHILD_UPDATED: {
      const changes = details.changes || 'record';
      return `${actor} updated student "${details.name || 'unknown'}": ${changes}`;
    }
    case AUDIT_ACTIONS.CHILD_DELETED:
      return `${actor} removed student "${details.name || 'unknown'}"`;
    case AUDIT_ACTIONS.CHILD_SCHEDULED: {
      if (details.action) {
        // Bulk apply
        return `${actor} applied recurring schedule: ${details.studentsProcessed || 0} students (${dateRange})`;
      }
      const time = details.time && details.time !== 'Full day' ? ` (${details.time})` : '';
      const type = details.type === 'Visit' ? ' (visit)' : '';
      return `${actor} scheduled ${details.student || 'student'} in ${details.room || 'room'} on ${date}${time}${type}`;
    }
    case AUDIT_ACTIONS.CHILD_UNSCHEDULED:
      return `${actor} removed ${details.student || 'student'} from ${details.room || 'room'} on ${date}`;
    case AUDIT_ACTIONS.CHILD_SCHEDULE_CLEARED:
      return `${actor} cleared all student assignments for ${dateRange || date} (${details.studentsCleared || 0} students)`;
    case AUDIT_ACTIONS.CHILD_VISIT_SCHEDULED:
      return `${actor} scheduled visit for ${details.student || 'student'} in ${details.room || 'room'} on ${date}`;
    
    // Template management
    case AUDIT_ACTIONS.TEMPLATE_CREATED:
      return `${actor} created schedule template "${details.name || 'unknown'}"`;
    case AUDIT_ACTIONS.TEMPLATE_UPDATED: {
      const changes = details.changes || 'template';
      return `${actor} updated template "${details.name}": ${changes}`;
    }
    case AUDIT_ACTIONS.TEMPLATE_DELETED:
      return `${actor} deleted template "${details.name || 'unknown'}"`;
    case AUDIT_ACTIONS.TEMPLATE_APPLIED:
      return `${actor} applied template "${details.templateName || 'unknown'}" to week of ${formatDateForDisplay(details.targetWeek)}`;
    
    default:
      return `${actor} performed ${entry.action?.replace(/_/g, ' ').toLowerCase() || 'action'}`;
  }
}

async function addAuditLog(entry) {
  try {
    // Generate description and add to details
    const description = generateAuditDescription(entry);
    const enrichedEntry = {
      ...entry,
      details: {
        ...entry.details,
        description
      }
    };
    await auditService.add(enrichedEntry);
    
    // Add to cache immediately so it shows up without refresh
    const cacheEntry = {
      id: Date.now().toString(), // Temp ID, will be replaced on next refresh
      actorId: entry.userId,
      actorName: entry.userName || 'System',
      action: entry.action,
      targetType: entry.targetType,
      targetId: entry.targetId,
      details: enrichedEntry.details,
      description: description,
      timestamp: new Date().toISOString()
    };
    cache.auditLog.unshift(cacheEntry);
  } catch (error) {
    console.error('Error adding audit log:', error);
  }
}

function getAuditLog(options = {}) {
  // Return cached audit log synchronously
  // Handle both old format (number) and new format (options object)
  const limit = typeof options === 'number' ? options : (options.limit || 500);
  return cache.auditLog.slice(0, limit);
}

// Async version to refresh from database
async function refreshAuditLog() {
  // Auto-cleanup entries older than 90 days
  await auditService.cleanup(90);
  
  const logs = await auditService.getAll();  // Fetches up to 5000 entries
  cache.auditLog = logs;
  return logs;
}

// ============================================================================
// TEMPLATE METHODS
// ============================================================================

function getTemplates() {
  return cache.templates;
}

function getTemplateById(id) {
  return cache.templates.find(t => t.id === id) || null;
}

async function createTemplate(templateData, createdBy = null) {
  const newTemplate = await templateService.create({
    ...templateData,
    createdBy: createdBy?.id
  });
  cache.templates.push(newTemplate);
  
  await addAuditLog({
    action: AUDIT_ACTIONS.TEMPLATE_CREATED,
    userId: createdBy?.id,
    userName: createdBy?.name,
    targetType: 'template',
    targetId: newTemplate.id,
    details: { name: templateData.name }
  });
  
  return newTemplate;
}

async function updateTemplate(id, updates, updatedBy = null) {
  const existingTemplate = cache.templates.find(t => t.id === id);
  
  // Helper to build change description
  const buildTemplateChanges = (existing, upd) => {
    const changes = [];
    if (upd.name && upd.name !== existing?.name) {
      changes.push(`renamed to "${upd.name}"`);
    }
    if (upd.description !== undefined && upd.description !== existing?.description) {
      changes.push('description updated');
    }
    if (upd.scheduleData && JSON.stringify(upd.scheduleData) !== JSON.stringify(existing?.scheduleData)) {
      changes.push('schedule modified');
    }
    return changes.length > 0 ? changes.join(', ') : 'template updated';
  };
  
  const template = await templateService.update(id, updates);
  const index = cache.templates.findIndex(t => t.id === id);
  if (index >= 0) {
    cache.templates[index] = { ...cache.templates[index], ...template };
  }
  
  await addAuditLog({
    action: AUDIT_ACTIONS.TEMPLATE_UPDATED,
    userId: updatedBy?.id,
    userName: updatedBy?.name,
    targetType: 'template',
    targetId: id,
    details: { 
      name: existingTemplate?.name || updates.name || 'Unknown',
      changes: buildTemplateChanges(existingTemplate, updates)
    }
  });
  
  return template;
}

async function deleteTemplate(id, deletedBy = null) {
  const template = cache.templates.find(t => t.id === id);
  await templateService.delete(id);
  cache.templates = cache.templates.filter(t => t.id !== id);
  
  await addAuditLog({
    action: AUDIT_ACTIONS.TEMPLATE_DELETED,
    userId: deletedBy?.id,
    userName: deletedBy?.name,
    targetType: 'template',
    targetId: id,
    details: { name: template?.name }
  });
  
  return true;
}

/**
 * Helper: Get date string for a day of the week given Monday's date
 * @param {string} mondayDate - Monday date (YYYY-MM-DD)
 * @param {number} dayIndex - 0=Monday, 1=Tuesday, etc.
 * @returns {string} Date string in YYYY-MM-DD format
 */
function getDateForDayOfWeek(mondayDate, dayIndex) {
  const date = new Date(mondayDate + 'T12:00:00');
  date.setDate(date.getDate() + dayIndex);
  return date.toISOString().split('T')[0];
}

/**
 * Create a template from an existing week's schedule
 * @param {string} mondayDate - Monday of the week to capture (YYYY-MM-DD)
 * @param {string} name - Template name
 * @param {string} description - Template description
 * @param {Object} actor - User creating the template
 * @returns {Object} Created template
 */
async function createTemplateFromWeek(mondayDate, name, description, actor = null) {
  const weekSchedule = getScheduleForWeek(mondayDate);
  const assignments = {};
  
  // Convert actual dates to day-of-week indices (0=Monday, 4=Friday)
  for (let dayIndex = 0; dayIndex < 5; dayIndex++) {
    const dateStr = getDateForDayOfWeek(mondayDate, dayIndex);
    const daySchedule = weekSchedule[dateStr];
    
    if (daySchedule) {
      assignments[dayIndex] = {};
      
      Object.keys(daySchedule).forEach(roomId => {
        const roomAssignments = daySchedule[roomId];
        if (roomAssignments && roomAssignments.length > 0) {
          assignments[dayIndex][roomId] = roomAssignments.map(a => ({
            userId: a.userId,
            startTime: a.startTime,
            endTime: a.endTime
          }));
        }
      });
    }
  }
  
  return await createTemplate({
    name,
    description,
    assignments
  }, actor);
}

/**
 * Get template summary statistics
 * @param {string} templateId - Template ID
 * @returns {Object} Stats about the template
 */
function getTemplateStats(templateId) {
  const template = getTemplateById(templateId);
  if (!template) return null;
  
  let totalAssignments = 0;
  const roomsCovered = new Set();
  const teachersIncluded = new Set();
  const daysWithAssignments = new Set();
  
  Object.keys(template.assignments || {}).forEach(dayIndex => {
    const dayAssignments = template.assignments[dayIndex];
    if (Object.keys(dayAssignments).length > 0) {
      daysWithAssignments.add(dayIndex);
    }
    
    Object.keys(dayAssignments).forEach(roomId => {
      roomsCovered.add(roomId);
      dayAssignments[roomId].forEach(a => {
        totalAssignments++;
        teachersIncluded.add(a.userId);
      });
    });
  });
  
  return {
    totalAssignments,
    roomsCovered: roomsCovered.size,
    teachersIncluded: teachersIncluded.size,
    daysWithAssignments: daysWithAssignments.size
  };
}

/**
 * Preview what applying a template would create
 * Returns conflicts and what would be created
 * @param {string} templateId - Template ID
 * @param {string} targetMondayDate - Monday of target week (YYYY-MM-DD)
 * @returns {Object} Preview with conflicts and assignments
 */
function previewTemplateApplication(templateId, targetMondayDate) {
  const template = getTemplateById(templateId);
  if (!template) return null;
  
  const conflicts = [];
  const wouldCreate = [];
  const skippedPTO = [];
  
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  
  // Check each day in the template
  Object.keys(template.assignments || {}).forEach(dayIndexStr => {
    const dayIndex = parseInt(dayIndexStr);
    const dateStr = getDateForDayOfWeek(targetMondayDate, dayIndex);
    const dayAssignments = template.assignments[dayIndexStr];
    
    Object.keys(dayAssignments).forEach(roomId => {
      const roomAssignments = dayAssignments[roomId];
      const room = getRoomById(roomId);
      const roomName = room?.name || 'Unknown Room';
      
      roomAssignments.forEach(assignment => {
        const user = getUserById(assignment.userId);
        const userName = user?.name || 'Unknown User';
        
        // Check if user has PTO on this date
        if (isUserOnPTO(assignment.userId, dateStr)) {
          skippedPTO.push({
            date: dateStr,
            dayName: dayNames[dayIndex],
            userName,
            roomName
          });
          return;
        }
        
        // Check if there's already an assignment for this user on this date
        const existingSchedule = getScheduleForDate(dateStr);
        let hasExisting = false;
        
        if (existingSchedule) {
          Object.values(existingSchedule).forEach(roomSchedule => {
            if (roomSchedule.some(a => a.userId === assignment.userId)) {
              hasExisting = true;
            }
          });
        }
        
        if (hasExisting) {
          conflicts.push({
            date: dateStr,
            dayName: dayNames[dayIndex],
            userName,
            roomName,
            type: 'existing_assignment'
          });
        }
        
        wouldCreate.push({
          date: dateStr,
          dayName: dayNames[dayIndex],
          roomId,
          roomName,
          userId: assignment.userId,
          userName,
          startTime: assignment.startTime,
          endTime: assignment.endTime
        });
      });
    });
  });
  
  return {
    template,
    targetWeek: targetMondayDate,
    conflicts,
    skippedPTO,
    wouldCreate,
    totalAssignments: wouldCreate.length
  };
}

/**
 * Helper: Remove all assignments for a user on a specific date
 * @param {string} userId - User ID
 * @param {string} dateStr - Date string (YYYY-MM-DD)
 */
async function removeUserAssignmentsForDate(userId, dateStr) {
  const daySchedule = cache.schedule[dateStr];
  if (!daySchedule) return;
  
  for (const roomId of Object.keys(daySchedule)) {
    const assignments = daySchedule[roomId] || [];
    const userAssignments = assignments.filter(a => a.userId === userId);
    
    for (const assignment of userAssignments) {
      // removeScheduleAssignment(date, roomId, assignmentId, removedBy)
      await removeScheduleAssignment(dateStr, roomId, assignment.id);
    }
  }
}

/**
 * Apply a template to a target week
 * @param {string} templateId - Template ID
 * @param {string} targetMondayDate - Monday of target week (YYYY-MM-DD)
 * @param {Object} options - { skipPTO: true, overwriteExisting: false, respectAvailability: true }
 * @param {Object} actor - User applying the template
 * @returns {Object} Result with created count and skipped items
 */
async function applyTemplate(templateId, targetMondayDate, options = {}, actor = null) {
  const {
    skipPTO = true,
    overwriteExisting = false,
    respectAvailability = true
  } = options;
  
  const template = getTemplateById(templateId);
  console.log('Applying template:', { templateId, template, targetMondayDate });
  
  if (!template) return { success: false, error: 'Template not found' };
  if (!template.assignments || Object.keys(template.assignments).length === 0) {
    return { success: false, error: 'Template has no assignments' };
  }
  
  const created = [];
  const skipped = [];
  
  const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  
  // Process each day in the template
  console.log('Template assignments keys:', Object.keys(template.assignments || {}));
  for (const dayIndexStr of Object.keys(template.assignments || {})) {
    const dayIndex = parseInt(dayIndexStr);
    const dateStr = getDateForDayOfWeek(targetMondayDate, dayIndex);
    const dayAssignments = template.assignments[dayIndexStr];
    console.log('Processing day:', { dayIndexStr, dayIndex, dateStr, dayAssignments });
    
    for (const roomId of Object.keys(dayAssignments || {})) {
      const roomAssignments = dayAssignments[roomId];
      console.log('Processing room:', { roomId, roomAssignments });
      
      for (const assignment of roomAssignments) {
        console.log('Processing assignment:', assignment);
        const user = getUserById(assignment.userId);
        const room = getRoomById(roomId);
        console.log('Found user/room:', { user: user?.name, room: room?.name, userId: assignment.userId, roomId });
        
        // Skip if user doesn't exist anymore
        if (!user) {
          console.log('SKIPPED: user not found', assignment.userId);
          skipped.push({
            date: dateStr,
            reason: 'user_not_found',
            userId: assignment.userId
          });
          continue;
        }
        
        // Skip if room doesn't exist anymore
        if (!room) {
          console.log('SKIPPED: room not found', roomId);
          skipped.push({
            date: dateStr,
            reason: 'room_not_found',
            roomId: roomId
          });
          continue;
        }
        
        // Skip if user has PTO
        if (skipPTO && isUserOnPTO(assignment.userId, dateStr)) {
          console.log('SKIPPED: user has PTO', user.name, dateStr);
          skipped.push({
            date: dateStr,
            reason: 'pto',
            userName: user.name
          });
          continue;
        }
        
        // Check availability if requested
        if (respectAvailability) {
          const dayName = dayNames[dayIndex];
          const availability = user.availability?.[dayName];
          console.log('Checking availability:', { dayName, availability });
          
          // Only skip if availability is explicitly set to unavailable
          // If availability is undefined/null, treat as available (default)
          if (availability && availability.available === false) {
            console.log('SKIPPED: not available', user.name, dayName);
            skipped.push({
              date: dateStr,
              reason: 'not_available',
              userName: user.name
            });
            continue;
          }
        }
        
        // Check for existing assignment
        const existingSchedule = getScheduleForDate(dateStr);
        let hasExisting = false;
        
        if (existingSchedule) {
          Object.values(existingSchedule).forEach(roomSchedule => {
            if (roomSchedule.some(a => a.userId === assignment.userId)) {
              hasExisting = true;
            }
          });
        }
        console.log('Existing check:', { dateStr, hasExisting, overwriteExisting });
        
        if (hasExisting && !overwriteExisting) {
          console.log('SKIPPED: existing assignment', user.name, dateStr);
          skipped.push({
            date: dateStr,
            reason: 'existing_assignment',
            userName: user.name
          });
          continue;
        }
        
        // If overwriting, remove existing assignments for this user on this date
        if (hasExisting && overwriteExisting) {
          await removeUserAssignmentsForDate(assignment.userId, dateStr);
        }
        
        // Create the assignment
        try {
          console.log('CREATING assignment:', { dateStr, roomId, userId: assignment.userId, startTime: assignment.startTime, endTime: assignment.endTime });
          await addScheduleAssignment(
            dateStr,
            roomId,
            assignment.userId,
            assignment.startTime,
            assignment.endTime,
            actor
          );
          
          console.log('SUCCESS: assignment created for', user.name, 'in', room.name, 'on', dateStr);
          created.push({
            date: dateStr,
            roomName: room.name,
            userName: user.name,
            startTime: assignment.startTime,
            endTime: assignment.endTime
          });
        } catch (err) {
          console.error('Error creating assignment:', err, { dateStr, roomId, userId: assignment.userId });
          skipped.push({
            date: dateStr,
            reason: 'error',
            userName: user.name,
            error: err.message
          });
        }
      }
    }
  }
  
  await addAuditLog({
    action: AUDIT_ACTIONS.TEMPLATE_APPLIED,
    userId: actor?.id,
    userName: actor?.name,
    targetType: 'template',
    targetId: templateId,
    details: {
      templateName: template.name,
      targetWeek: targetMondayDate,
      assignmentsCreated: created.length,
      assignmentsSkipped: skipped.length
    }
  });
  
  return {
    success: true,
    created,
    skipped,
    createdCount: created.length,
    skippedCount: skipped.length
  };
}


// ============================================================================
// UTILITY METHODS
// ============================================================================

/**
 * Get users who are on approved PTO for a given date
 */
function getUsersOnPTO(date) {
  const approvedPTO = cache.ptoRequests.filter(p => 
    p.status === 'Approved' && p.dates.includes(date)
  );
  return approvedPTO.map(p => {
    const user = cache.users.find(u => u.id === p.userId);
    return {
      ...p,
      user
    };
  });
}

/**
 * Check if a user has approved PTO for a specific date
 */
function isUserOnPTO(userId, date) {
  return cache.ptoRequests.some(p => 
    p.userId === userId && 
    p.status === 'Approved' && 
    p.dates.includes(date)
  );
}

/**
 * Check if user has FULL day PTO (not partial) on a date
 */
function hasFullDayPTO(userId, dateStr) {
  const pto = cache.ptoRequests.find(p => 
    p.userId === userId && 
    p.status === 'Approved' && 
    p.dates.includes(dateStr)
  );
  if (!pto) return false;
  // If it's partial day PTO, they're still partially available
  return !pto.isPartialDay;
}

/**
 * Check if user is available on a specific date
 * Returns true if user has availability set AND doesn't have full-day PTO
 */
function isUserAvailable(userId, dateStr) {
  // Check if user has full-day PTO
  if (hasFullDayPTO(userId, dateStr)) return false;
  
  // Check user's availability settings
  const user = cache.users.find(u => u.id === userId);
  if (!user) return true; // Default to available if user not found
  
  const date = new Date(dateStr + 'T12:00:00');
  const dayIndex = date.getDay(); // 0=Sun, 1=Mon, etc.
  
  // For ADMIN users with profile availability settings, they're available M-F
  if (user.role === 'ADMIN' && (user.availabilityStart || user.availabilityEnd)) {
    // Available Monday-Friday (dayIndex 1-5), not weekends
    return dayIndex >= 1 && dayIndex <= 5;
  }
  
  // For teachers, check their detailed availability
  if (!user.availability) return true; // Default to available if no settings
  
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayName = dayNames[dayIndex];
  
  const dayAvail = user.availability[dayName];
  if (!dayAvail) return true; // No setting for this day = available
  
  return dayAvail.available !== false;
}

/**
 * Get user's availability settings for a specific date
 * Returns { available: boolean, start: string, end: string, slots: array }
 * 
 * For ADMIN users with availabilityStart/End set in their profile,
 * those times are used as their M-F availability.
 */
function getUserAvailabilityForDate(userId, dateStr) {
  const user = cache.users.find(u => u.id === userId);
  if (!user) return null;
  
  const date = new Date(dateStr + 'T12:00:00');
  const dayIndex = date.getDay();
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayName = dayNames[dayIndex];
  
  // For ADMIN users with profile availability settings, use those for weekdays
  if (user.role === 'ADMIN' && (user.availabilityStart || user.availabilityEnd)) {
    // Weekend = not available
    if (dayIndex === 0 || dayIndex === 6) {
      return { available: false, start: null, end: null, slots: [] };
    }
    // Weekday = use their profile availability times
    return {
      available: true,
      start: user.availabilityStart || '08:00',
      end: user.availabilityEnd || '17:00',
      slots: []
    };
  }
  
  const dayAvail = user.availability?.[dayName];
  if (!dayAvail) {
    // Default availability
    return { available: true, start: '08:00', end: '17:00', slots: [] };
  }
  
  return {
    available: dayAvail.available !== false,
    start: dayAvail.start || '08:00',
    end: dayAvail.end || '17:00',
    slots: dayAvail.slots || []
  };
}

/**
 * Get PTO details for a user on a specific date
 * Returns null if no PTO, or { isPartialDay, startTime, endTime, hours, reason }
 */
function getPTODetailsForDate(userId, dateStr) {
  const pto = cache.ptoRequests.find(p => 
    p.userId === userId && 
    p.status === 'Approved' && 
    p.dates.includes(dateStr)
  );
  
  if (!pto) return null;
  
  if (pto.isPartialDay) {
    // Calculate end time from start time and hours
    const [h, m] = (pto.partialStartTime || '08:00').split(':').map(Number);
    const endHours = h + (pto.partialHours || 4);
    const endTime = `${String(endHours).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    
    return {
      isPartialDay: true,
      startTime: pto.partialStartTime || '08:00',
      endTime: endTime,
      hours: pto.partialHours || 4,
      reason: pto.reason
    };
  }
  
  return {
    isPartialDay: false,
    reason: pto.reason
  };
}

/**
 * Calculate child's age from date of birth
 * Returns object with weeks, months, years, and display string
 */
function calculateChildAge(dob) {
  if (!dob) return null;
  
  const birthDate = new Date(dob + 'T12:00:00');
  const today = new Date();
  
  const diffMs = today - birthDate;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30.44);
  const diffYears = Math.floor(diffDays / 365.25);
  
  let displayText;
  if (diffMonths < 1) {
    displayText = `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''}`;
  } else if (diffMonths < 24) {
    displayText = `${diffMonths} month${diffMonths !== 1 ? 's' : ''}`;
  } else {
    const years = Math.floor(diffMonths / 12);
    const remainingMonths = diffMonths % 12;
    if (remainingMonths === 0) {
      displayText = `${years} year${years !== 1 ? 's' : ''}`;
    } else {
      displayText = `${years}y ${remainingMonths}m`;
    }
  }
  
  return {
    weeks: diffWeeks,
    months: diffMonths,
    years: diffYears,
    days: diffDays,
    displayText
  };
}

/**
 * Get room ratio information for a date (teacher count vs child count)
 */
function getRoomRatioInfo(date, roomId) {
  const room = cache.rooms.find(r => r.id === roomId);
  const teacherAssignments = cache.schedule[date]?.[roomId] || [];
  
  // Count children in this room (child schedule is { childId: { roomId } })
  const daySchedule = cache.childSchedule[date] || {};
  let childCount = 0;
  Object.values(daySchedule).forEach(childEntry => {
    if (childEntry.roomId === roomId) {
      childCount++;
    }
  });
  
  return {
    room,
    teacherCount: teacherAssignments.length,
    childCount: childCount,
    ratio: room?.ratio || 4,
    isOverRatio: childCount > (teacherAssignments.length * (room?.ratio || 4))
  };
}

/**
 * Get count of children in a room on a date
 * Excludes absent children and stale schedule rows for children who are no
 * longer active (e.g. withdrawn), so this always matches the number of
 * green/orange student tags actually rendered for that room/day.
 */
function getChildCountInRoom(roomId, dateStr) {
  // Child schedule is stored as { date: { childId: { roomId, ... } } }
  // We need to count children whose roomId matches and are not absent
  const daySchedule = cache.childSchedule[dateStr] || {};
  let count = 0;
  Object.entries(daySchedule).forEach(([childId, childEntry]) => {
    if (childEntry.roomId !== roomId || childEntry.isAbsent) return;
    const child = cache.children.find(c => c.id === childId);
    if (child && child.status?.toLowerCase() === 'active') {
      count++;
    }
  });
  return count;
}

/**
 * Get count of teachers in a room on a date
 */
function getTeacherCountInRoom(roomId, dateStr) {
  const assignments = cache.schedule[dateStr]?.[roomId] || [];
  return assignments.length;
}

/**
 * Calculate ratio status for a room on a date
 */
function calculateRoomRatio(roomId, dateStr) {
  const room = cache.rooms.find(r => r.id === roomId);
  if (!room) return null;
  
  const childCount = getChildCountInRoom(roomId, dateStr);
  const teacherCount = getTeacherCountInRoom(roomId, dateStr);
  const requiredRatio = room.ratio;
  
  // Get teacher assignments for coverage gap detection
  const teacherAssignments = cache.schedule[dateStr]?.[roomId] || [];
  
  // Check for coverage gaps (times when children present but no teacher)
  let hasCoverageGap = false;
  let coverageDetails = '';
  
  if (childCount > 0 && teacherAssignments.length > 0) {
    // Get child schedule entries for this room (exclude absent children)
    const daySchedule = cache.childSchedule[dateStr] || {};
    let earliestChildStart = '17:00';
    let latestChildEnd = '08:00';
    
    // Find the time range when children are present (excluding absent)
    Object.values(daySchedule).forEach(childEntry => {
      if (childEntry.roomId === roomId && !childEntry.isAbsent) {
        const childStart = childEntry.startTime || '08:00';
        const childEnd = childEntry.endTime || '17:00';
        if (childStart < earliestChildStart) earliestChildStart = childStart;
        if (childEnd > latestChildEnd) latestChildEnd = childEnd;
      }
    });
    
    // Check if teachers cover this entire time range
    // Build a timeline of teacher coverage
    const timeToMinutes = (t) => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };
    
    const minutesToTime = (mins) => {
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      const period = h >= 12 ? 'PM' : 'AM';
      const hour12 = h === 0 ? 12 : (h > 12 ? h - 12 : h);
      return m === 0 ? `${hour12} ${period}` : `${hour12}:${String(m).padStart(2, '0')} ${period}`;
    };
    
    const childStartMins = timeToMinutes(earliestChildStart);
    const childEndMins = timeToMinutes(latestChildEnd);
    
    // Create coverage array (true = covered, false = gap)
    const coverage = new Array(childEndMins - childStartMins).fill(false);
    
    teacherAssignments.forEach(assignment => {
      const start = timeToMinutes(assignment.startTime || '08:00');
      const end = timeToMinutes(assignment.endTime || '17:00');
      
      for (let i = Math.max(0, start - childStartMins); i < Math.min(coverage.length, end - childStartMins); i++) {
        coverage[i] = true;
      }
    });
    
    // Find gaps
    const gaps = [];
    let gapStart = null;
    
    for (let i = 0; i < coverage.length; i++) {
      if (!coverage[i] && gapStart === null) {
        gapStart = i;
      } else if (coverage[i] && gapStart !== null) {
        gaps.push({
          start: minutesToTime(childStartMins + gapStart),
          end: minutesToTime(childStartMins + i)
        });
        gapStart = null;
      }
    }
    
    // Check if gap extends to the end
    if (gapStart !== null) {
      gaps.push({
        start: minutesToTime(childStartMins + gapStart),
        end: minutesToTime(childEndMins)
      });
    }
    
    if (gaps.length > 0) {
      hasCoverageGap = true;
      coverageDetails = gaps.map(g => `${g.start}-${g.end}`).join(', ');
    }
  } else if (childCount > 0 && teacherAssignments.length === 0) {
    // Children but no teachers at all
    hasCoverageGap = true;
    coverageDetails = 'No teachers assigned';
  }
  
  // If no ratio is set for the room, always valid
  if (!requiredRatio) {
    return {
      childCount,
      teacherCount,
      requiredRatio: null,
      maxChildren: null,
      actualRatio: teacherCount > 0 ? (childCount / teacherCount) : (childCount > 0 ? Infinity : 0),
      isValid: true,
      capacityUsed: 0,
      noRatioSet: true,
      hasCoverageGap,
      coverageDetails
    };
  }
  
  const maxChildren = teacherCount * requiredRatio;
  const actualRatio = teacherCount > 0 ? (childCount / teacherCount) : (childCount > 0 ? Infinity : 0);
  
  return {
    childCount,
    teacherCount,
    requiredRatio,
    maxChildren,
    actualRatio: Math.round(actualRatio * 10) / 10,
    isValid: childCount <= maxChildren,
    capacityUsed: maxChildren > 0 ? Math.round((childCount / maxChildren) * 100) : 0,
    noRatioSet: false,
    hasCoverageGap,
    coverageDetails
  };
}

/**
 * Validate all room ratios for a date
 */
function validateDayRatios(dateStr) {
  const rooms = cache.rooms;
  const violations = [];
  
  for (const room of rooms) {
    const status = calculateRoomRatio(room.id, dateStr);
    if (status && !status.isValid) {
      violations.push({ room, ratioStatus: status });
    }
  }
  
  return violations;
}

// ============================================================================
// ANNOUNCEMENTS
// ============================================================================

const ANNOUNCEMENTS_KEY = 'cplc_announcements';

function getAnnouncementsFromStorage() {
  try {
    const data = localStorage.getItem(ANNOUNCEMENTS_KEY);
    return data ? JSON.parse(data) : [];
  } catch (e) {
    console.error('Error reading announcements:', e);
    return [];
  }
}

function saveAnnouncementsToStorage(announcements) {
  try {
    localStorage.setItem(ANNOUNCEMENTS_KEY, JSON.stringify(announcements));
  } catch (e) {
    console.error('Error saving announcements:', e);
  }
}

/**
 * Get announcements for a specific week
 * @param {string} weekStart - Monday date string (YYYY-MM-DD)
 * @returns {Array} Announcements for that week
 */
function getAnnouncementsForWeek(weekStart) {
  const announcements = getAnnouncementsFromStorage();
  
  // Calculate week end (Friday)
  const startDate = new Date(weekStart + 'T12:00:00');
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + 4); // Friday
  
  const startStr = weekStart;
  const endStr = endDate.toISOString().split('T')[0];
  
  return announcements.filter(a => a.date >= startStr && a.date <= endStr)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

/**
 * Get all announcements
 * @returns {Array} All announcements
 */
function getAllAnnouncements() {
  return getAnnouncementsFromStorage();
}

/**
 * Add a new announcement
 * @param {Object} announcement - { date, message, icon, createdBy }
 * @param {Object} currentUser - User creating the announcement
 * @returns {Object} Created announcement with id
 */
function addAnnouncement(announcement, currentUser = null) {
  const announcements = getAnnouncementsFromStorage();
  
  const newAnnouncement = {
    id: `ann_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    date: announcement.date,
    message: announcement.message,
    icon: announcement.icon || 'ℹ️',
    createdBy: announcement.createdBy || currentUser?.id || null,
    createdByName: announcement.createdByName || currentUser?.name || 'Admin',
    createdAt: new Date().toISOString(),
    updatedAt: null
  };
  
  announcements.push(newAnnouncement);
  saveAnnouncementsToStorage(announcements);
  
  // Audit log
  if (currentUser) {
    addAuditLog(
      AUDIT_ACTIONS.ANNOUNCEMENT_CREATED,
      currentUser.id,
      `Created announcement for ${announcement.date}: "${announcement.message.substring(0, 50)}${announcement.message.length > 50 ? '...' : ''}"`,
      { announcementId: newAnnouncement.id }
    );
  }
  
  return newAnnouncement;
}

/**
 * Update an announcement
 * @param {string} id - Announcement ID
 * @param {Object} updates - { message, icon, date }
 * @param {Object} currentUser - User making the update
 * @returns {Object|null} Updated announcement or null if not found
 */
function updateAnnouncement(id, updates, currentUser = null) {
  const announcements = getAnnouncementsFromStorage();
  const index = announcements.findIndex(a => a.id === id);
  
  if (index === -1) return null;
  
  announcements[index] = {
    ...announcements[index],
    ...updates,
    updatedAt: new Date().toISOString()
  };
  
  saveAnnouncementsToStorage(announcements);
  
  // Audit log
  if (currentUser) {
    addAuditLog(
      AUDIT_ACTIONS.ANNOUNCEMENT_UPDATED,
      currentUser.id,
      `Updated announcement: "${updates.message?.substring(0, 50) || announcements[index].message.substring(0, 50)}..."`,
      { announcementId: id }
    );
  }
  
  return announcements[index];
}

/**
 * Delete an announcement
 * @param {string} id - Announcement ID
 * @param {Object} currentUser - User deleting the announcement
 * @returns {boolean} Success
 */
function deleteAnnouncement(id, currentUser = null) {
  const announcements = getAnnouncementsFromStorage();
  const announcement = announcements.find(a => a.id === id);
  const filtered = announcements.filter(a => a.id !== id);
  
  if (filtered.length === announcements.length) return false;
  
  saveAnnouncementsToStorage(filtered);
  
  // Audit log
  if (currentUser && announcement) {
    addAuditLog(
      AUDIT_ACTIONS.ANNOUNCEMENT_DELETED,
      currentUser.id,
      `Deleted announcement: "${announcement.message.substring(0, 50)}${announcement.message.length > 50 ? '...' : ''}"`,
      { announcementId: id, date: announcement.date }
    );
  }
  
  return true;
}

/**
 * Clean up old announcements (older than 30 days)
 */
function cleanupOldAnnouncements() {
  const announcements = getAnnouncementsFromStorage();
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - 30);
  const cutoffStr = cutoffDate.toISOString().split('T')[0];
  
  const filtered = announcements.filter(a => a.date >= cutoffStr);
  
  if (filtered.length !== announcements.length) {
    saveAnnouncementsToStorage(filtered);
    console.log(`Cleaned up ${announcements.length - filtered.length} old announcements`);
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

const dataStore = {
  // Initialization
  initializeData,
  isInitialized,
  waitForInit,
  
  // Users
  getUsers,
  getUserById,
  getUserByUsername,
  createUser,
  updateUser,
  deleteUser,
  updateUserPassword,
  resetUserPassword: updateUserPassword,  // Alias for UserManagement.js
  verifyPassword,
  changeOwnPassword,
  updateUserAvailability,
  updateUserDarkMode,
  updateUserHeaderColor,
  
  // Authentication
  authenticate,
  logout,
  
  // Rooms
  getRooms,
  getRoomById,
  getVisibleRooms,
  createRoom,
  updateRoom,
  deleteRoom,
  updateRoomOrder,
  
  // Schedule
  getSchedule,
  getScheduleForDate,
  getScheduleForWeek,
  getWeekSchedule: getScheduleForWeek,  // Alias for ScheduleManagement.js
  getWeekDates,
  addScheduleAssignment,
  assignUserToRoom: addScheduleAssignment,  // Alias for ScheduleManagement.js
  removeScheduleAssignment,
  removeUserFromRoom: removeScheduleAssignment,  // Alias for ScheduleManagement.js
  clearScheduleForDay,
  refreshSchedule,
  isWeekPublished,
  setWeekPublished,
  getPublishedWeeks,
  
  // PTO
  getPTORequests,
  getPTORequestsByUser,
  getPTORequestById,
  getPendingPTORequests,
  getApprovedPTOForDate,
  getPTORequestsByStatus,
  createPTORequest,
  approvePTORequest,
  denyPTORequest,
  cancelPTORequest,
  
  // Children
  getChildren,
  getChildById,
  getActiveChildren,
  getChildrenByRoom,
  createChild,
  addChild: createChild,  // Alias for compatibility
  updateChild,
  deleteChild,
  
  // Child Schedule
  getChildSchedule,
  getChildScheduleForDate,
  getChildScheduleForWeek,
  getChildrenInRoom,
  getChildDaySchedule,
  getDayCode,
  assignChildToRoom,
  toggleChildAbsent,
  applyDefaultSchedule,
  addChildToSchedule,
  removeChildFromSchedule,
  removeChildFromDay,
  clearChildScheduleForDay,
  clearChildScheduleForDateRange,
  refreshChildScheduleForDateRange,
  moveChildToRoom,
  getChildrenScheduledForDate,
  
  // Notes
  getDailyNotes,
  getDailyNote,
  setDailyNote,
  getRoomDayNotes,
  getRoomDayNote,
  setRoomDayNote,
  addRoomDayNote,
  deleteRoomDayNote,
  
  // Audit
  addAuditLog,
  getAuditLog,
  refreshAuditLog,
  
  // Announcements
  getAnnouncementsForWeek,
  getAllAnnouncements,
  addAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  cleanupOldAnnouncements,
  
  // Templates
  getTemplates,
  getTemplateById,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  createTemplateFromWeek,
  getTemplateStats,
  previewTemplateApplication,
  applyTemplate,
  
  // Utilities
  getUsersOnPTO,
  isUserOnPTO,
  hasApprovedPTO: isUserOnPTO,  // Alias for ScheduleManagement.js
  hasFullDayPTO,
  isUserAvailable,
  getUserAvailabilityForDate,
  getPTODetailsForDate,
  calculateChildAge,
  getRoomRatioInfo,
  getChildCountInRoom,
  getTeacherCountInRoom,
  calculateRoomRatio,
  validateDayRatios,
  
  // Constants
  AUDIT_ACTIONS
};

// Named export for components that import AUDIT_ACTIONS directly
export { AUDIT_ACTIONS };

export default dataStore;
